/**
 * Player Ladder Page
 * Dedicated page for players to view and manage their ladder challenges
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { collection, getDocs, query, orderBy, where, getDoc, doc } from 'firebase/firestore';
import LadderChallengeModal from '../components/LadderChallengeModal';
import MyChallengesSection, { type Challenge } from '../components/MyChallengesSection';
import type { LadderEntry, LadderSettings } from '../types';
import type { PlayerRosterDocument } from '@schemas/firestore';

export default function PlayerLadderPage() {
    const { user, userDoc } = useAuth();
    const [loading, setLoading] = useState(true);
    const [playerData, setPlayerData] = useState<PlayerRosterDocument | null>(null);
    const [myLadderRank, setMyLadderRank] = useState<LadderEntry | null>(null);
    const [ladderRankings, setLadderRankings] = useState<LadderEntry[]>([]);
    const [ladderSettings, setLadderSettings] = useState<LadderSettings | null>(null);
    const [myChallenges, setMyChallenges] = useState<Challenge[]>([]);
    const [showChallengeModal, setShowChallengeModal] = useState(false);
    const [totalLadderPlayers, setTotalLadderPlayers] = useState(0);

    // Load ladder data
    useEffect(() => {
        async function loadData() {
            if (!user) return;

            try {
                // Get player roster data
                const rosterSnap = await getDocs(collection(db, 'playerRoster'));
                const players = rosterSnap.docs.map(d => ({ id: d.id, ...d.data() } as PlayerRosterDocument & { id: string }));
                const myPlayer = players.find(p =>
                    p.linkedUserId === user.uid ||
                    (userDoc?.email && p.parentEmail?.toLowerCase() === userDoc.email.toLowerCase())
                );
                setPlayerData(myPlayer || null);

                // Get ladder data
                const ladderSnap = await getDocs(query(collection(db, 'ssbuLadder'), orderBy('rank', 'asc')));
                const ladderData = ladderSnap.docs.map(d => ({ id: d.id, ...d.data() })) as LadderEntry[];
                setLadderRankings(ladderData);
                setTotalLadderPlayers(ladderData.length);

                // Find my rank - by roster ID, linkedUserId, or user.uid
                const myRank = ladderData.find(e =>
                    (myPlayer && e.playerRosterId === myPlayer.id) ||
                    e.linkedUserId === user.uid ||
                    e.playerRosterId === user.uid
                );
                setMyLadderRank(myRank || null);

                // Get ladder settings
                const settingsDoc = await getDoc(doc(db, 'settings', 'ssbuLadder'));
                if (settingsDoc.exists()) {
                    setLadderSettings(settingsDoc.data() as LadderSettings);
                }

                // Get my challenges
                const myLadderId = myRank?.playerRosterId || myPlayer?.id || user.uid;
                const challengesSnap = await getDocs(
                    query(
                        collection(db, 'ssbuChallenges'),
                        where('status', 'in', ['pending', 'accepted', 'awaiting_confirm', 'scheduled']),
                        orderBy('createdAt', 'desc')
                    )
                );
                const challengesData = challengesSnap.docs
                    .map(d => ({ id: d.id, ...d.data() }) as Challenge)
                    .filter(c => c.challengerId === myLadderId || c.defenderId === myLadderId);
                setMyChallenges(challengesData);

            } catch (err) {
                console.error('Error loading ladder data:', err);
            } finally {
                setLoading(false);
            }
        }
        loadData();
    }, [user, userDoc]);

    const refreshData = async () => {
        if (!user) return;

        const ladderSnap = await getDocs(query(collection(db, 'ssbuLadder'), orderBy('rank', 'asc')));
        const ladderData = ladderSnap.docs.map(d => ({ id: d.id, ...d.data() })) as LadderEntry[];
        setLadderRankings(ladderData);

        const refreshedRank = ladderData.find(e =>
            (playerData && e.playerRosterId === (playerData as PlayerRosterDocument & { id: string }).id) ||
            e.linkedUserId === user.uid ||
            e.playerRosterId === user.uid
        );
        setMyLadderRank(refreshedRank || null);

        const myLadderId = refreshedRank?.playerRosterId || (playerData as PlayerRosterDocument & { id: string })?.id || user.uid;
        const challengesSnap = await getDocs(
            query(
                collection(db, 'ssbuChallenges'),
                where('status', 'in', ['pending', 'accepted', 'awaiting_confirm', 'scheduled']),
                orderBy('createdAt', 'desc')
            )
        );
        const challengesData = challengesSnap.docs
            .map(d => ({ id: d.id, ...d.data() }) as Challenge)
            .filter(c => c.challengerId === myLadderId || c.defenderId === myLadderId);
        setMyChallenges(challengesData);
    };

    if (loading) {
        return (
            <div className="p-8 flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    const canChallenge = (ladderSettings?.challengeWindowOpen || (ladderSettings?.isPreseason && ladderSettings?.allowOpenChallenges)) && myLadderRank && myLadderRank.rank > 1;

    return (
        <div className="p-4 md:p-6 lg:p-8 max-w-5xl">
            {/* Header */}
            <div className="mb-6 animate-fade-in">
                <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3 mb-2">
                    <h1 className="text-3xl font-display font-bold text-white flex items-center gap-3">
                        <span>⚔️</span> SSBU Ladder
                    </h1>
                    <div className="flex flex-wrap gap-2">
                        {ladderSettings?.challengeWindowOpen && (
                            <span className="px-3 py-1 bg-green-500/20 text-green-400 text-sm rounded-full animate-pulse">
                                Challenge Window Open
                            </span>
                        )}
                        {ladderSettings?.isPreseason && !ladderSettings?.challengeWindowOpen && (
                            <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 text-sm rounded-full">
                                Preseason
                            </span>
                        )}
                    </div>
                </div>
                <p className="text-secondary-400 mt-1">
                    Challenge players above you to climb the ranks!
                </p>
            </div>

            {myLadderRank ? (
                <>
                    {/* My Rank Card */}
                    <div className="card mb-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                            <div className="flex items-center gap-4">
                                <div className={`text-5xl font-bold ${myLadderRank.rank === 1 ? 'text-yellow-400' :
                                    myLadderRank.rank <= 3 ? 'text-secondary-200' :
                                        'text-white'
                                    }`}>
                                    {myLadderRank.rank === 1 ? '👑' : `#${myLadderRank.rank}`}
                                </div>
                                <div>
                                    <div className="text-xl text-white font-semibold">
                                        {myLadderRank.gamerTag || myLadderRank.playerName}
                                    </div>
                                    <div className="text-sm text-secondary-400">
                                        Rank {myLadderRank.rank} of {totalLadderPlayers} players
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowChallengeModal(true)}
                                disabled={!canChallenge}
                                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed text-lg px-6 py-3 w-full md:w-auto"
                                title={myLadderRank.rank === 1 ? "You're #1! No one above to challenge." : !canChallenge ? "Challenge window is closed" : "Issue a challenge"}
                            >
                                ⚔️ Issue Challenge
                            </button>
                        </div>

                        {/* Stats */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center mb-4">
                            <div className="bg-secondary-800/50 rounded-lg p-3">
                                <div className="text-2xl font-bold text-white">{myLadderRank.stats.challengesWon}</div>
                                <div className="text-xs text-secondary-400">Challenges Won</div>
                            </div>
                            <div className="bg-secondary-800/50 rounded-lg p-3">
                                <div className="text-2xl font-bold text-white">{myLadderRank.stats.defensesWon}</div>
                                <div className="text-xs text-secondary-400">Defenses Won</div>
                            </div>
                            <div className="bg-secondary-800/50 rounded-lg p-3">
                                <div className="text-2xl font-bold text-white">#{myLadderRank.stats.bestRank}</div>
                                <div className="text-xs text-secondary-400">Best Rank</div>
                            </div>
                            <div className="bg-secondary-800/50 rounded-lg p-3">
                                <div className={`text-2xl font-bold ${myLadderRank.stats.currentStreak > 0 ? 'text-orange-400' :
                                    myLadderRank.stats.currentStreak < 0 ? 'text-blue-400' :
                                        'text-secondary-400'
                                    }`}>
                                    {myLadderRank.stats.currentStreak > 0 ? `🔥${myLadderRank.stats.currentStreak}` :
                                        myLadderRank.stats.currentStreak < 0 ? `❄️${Math.abs(myLadderRank.stats.currentStreak)}` :
                                            '—'}
                                </div>
                                <div className="text-xs text-secondary-400">Streak</div>
                            </div>
                        </div>

                        {/* #1 Rank Notice */}
                        {myLadderRank.rank === 1 && (
                            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 text-center">
                                <div className="text-3xl mb-2">👑</div>
                                <p className="text-yellow-400 font-semibold">You're the Champion!</p>
                                <p className="text-secondary-400 text-sm mt-1">
                                    Defend your title when others challenge you. Keep checking back for incoming challenges!
                                </p>
                            </div>
                        )}

                        {/* Challenge Info */}
                        {myLadderRank.rank > 1 && (
                            <div className="mt-4 pt-4 border-t border-secondary-700 text-sm text-secondary-400">
                                <p>
                                    <strong className="text-white">Challenge Range:</strong> You can challenge up to{' '}
                                    {myLadderRank.rank <= (ladderSettings?.topTierThreshold || 5) ? '1 rank' : '3 ranks'} above you.
                                    {!ladderSettings?.challengeWindowOpen && !(ladderSettings?.isPreseason && ladderSettings?.allowOpenChallenges) && (
                                        <span className="ml-2 text-yellow-400">Window is currently closed.</span>
                                    )}
                                    {ladderSettings?.isPreseason && ladderSettings?.allowOpenChallenges && !ladderSettings?.challengeWindowOpen && (
                                        <span className="ml-2 text-green-400">⚡ Open challenges allowed (preseason)</span>
                                    )}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* My Challenges Section */}
                    {myChallenges.length > 0 && (
                        <div className="card mb-6">
                            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                                <span>📋</span> My Active Challenges
                            </h2>
                            <MyChallengesSection
                                playerId={myLadderRank.playerRosterId}
                                challenges={myChallenges}
                                rankings={ladderRankings}
                                settings={ladderSettings}
                                onRefresh={refreshData}
                            />
                        </div>
                    )}

                    {/* Full Ladder Rankings */}
                    <div className="card">
                        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                            <span>📊</span> Full Ladder Rankings
                        </h2>
                        <div className="space-y-2">
                            {ladderRankings.map(player => (
                                <div
                                    key={player.id}
                                    className={`flex items-center justify-between p-3 rounded-lg ${player.playerRosterId === myLadderRank.playerRosterId
                                        ? 'bg-primary-500/20 border border-primary-500/30'
                                        : 'bg-secondary-800/50'
                                        }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={`w-8 text-center font-bold ${player.rank === 1 ? 'text-yellow-400' :
                                            player.rank <= 3 ? 'text-secondary-200' :
                                                'text-white'
                                            }`}>
                                            {player.rank === 1 ? '👑' : `#${player.rank}`}
                                        </div>
                                        <div>
                                            <span className="text-white font-medium">{player.gamerTag || player.playerName}</span>
                                            {player.playerRosterId === myLadderRank.playerRosterId && (
                                                <span className="ml-2 text-xs text-primary-400">(You)</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="text-sm text-secondary-400">
                                        {player.stats.challengesWon}W - {player.stats.defensesWon}D
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            ) : (
                /* Not on ladder */
                <div className="card text-center py-12">
                    <div className="text-6xl mb-4">🎮</div>
                    <h2 className="text-xl font-semibold text-white mb-2">You're not on the ladder yet</h2>
                    <p className="text-secondary-400 mb-6">
                        Contact Coach to be added to the SSBU ladder.
                    </p>
                    <Link
                        to="/ladder"
                        className="btn-secondary inline-block"
                    >
                        View Public Ladder →
                    </Link>
                </div>
            )}

            {/* Challenge Modal */}
            {myLadderRank && (
                <LadderChallengeModal
                    isOpen={showChallengeModal}
                    onClose={() => setShowChallengeModal(false)}
                    playerId={myLadderRank.playerRosterId}
                    playerName={myLadderRank.gamerTag || myLadderRank.playerName}
                />
            )}
        </div>
    );
}
